/**
 * API Client Configuration
 * Base Axios instance with interceptors for authentication and error handling
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getApiUrl, isDebugMode } from '@/utils/env';
import { comparisonDiagnostic } from './comparison-diagnostics';
import { redactSensitiveData, redactSensitiveUrl } from '@/utils/redact';
import { getAccessToken, refreshAccessToken } from '@/utils/token';
import { buildLoginRedirectUrl } from '@/utils/returnTo';
import { useAuthStore } from '@/store/auth.store';
import { assertSessionOwner, getSessionOwner, ownsSession } from '@/utils/session';

declare module 'axios' {
  interface AxiosRequestConfig {
    _sessionOwner?: number;
    _capturedLogout?: boolean;
    _retry?: boolean;
  }
}

const expireSession = (owner: number) => {
  if (!ownsSession(owner)) return;
  useAuthStore.getState().logout();
  if (typeof window !== 'undefined') {
    window.location.href = buildLoginRedirectUrl(window.location, 'expired');
  }
};

/**
 * Create the base Axios instance
 */
const resolvedBaseUrl = getApiUrl();

export const apiClient: AxiosInstance = axios.create({
  baseURL: resolvedBaseUrl,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

/**
 * Request Interceptor
 * Adds authentication token to requests if available
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config._sessionOwner ??= getSessionOwner();
    if (!config._capturedLogout) assertSessionOwner(config._sessionOwner);
    // Log request in debug mode
    if (isDebugMode()) {
      const comparison = comparisonDiagnostic(config.url, config.method);
      if (comparison) console.log('[API Request]', comparison);
      else
        console.log(
          `[API Request] ${config.method?.toUpperCase()} ${redactSensitiveUrl(config.url)}`,
          {
            params: redactSensitiveData(config.params),
            data: redactSensitiveData(config.data),
          }
        );
    }

    // Get token from token utilities
    const token = getAccessToken();

    if (token && config.headers && !config._capturedLogout) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // An adapter can bind a request to the workspace that was active when the
    // operation started. Keep that binding authoritative across interceptors
    // and 401 retries; the mutable store is only a fallback.
    const workspaceId = useAuthStore.getState().currentWorkspaceId;
    if (workspaceId && config.headers && !config.headers.has('X-Workspace-Id')) {
      config.headers.set('X-Workspace-Id', String(workspaceId));
    }

    return config;
  },
  (error: AxiosError) => {
    if (isDebugMode()) {
      const comparison = comparisonDiagnostic(
        error.config?.url,
        error.config?.method,
        error.response?.status,
        error.response?.data
      );
      if (comparison) console.error('[API Request Error]', comparison);
      else
        console.error('[API Request Error]', {
          message: error.message,
          url: redactSensitiveUrl(error.config?.url),
          status: error.response?.status,
        });
    }
    throw error;
  },
  { synchronous: true }
);

/**
 * Response Interceptor
 * Handles authentication errors and logging
 */
apiClient.interceptors.response.use(
  (response) => {
    if (response.config._sessionOwner !== undefined)
      assertSessionOwner(response.config._sessionOwner);
    // Log response in debug mode
    if (isDebugMode()) {
      const comparison = comparisonDiagnostic(
        response.config.url,
        response.config.method,
        response.status,
        response.data
      );
      if (comparison) console.log('[API Response]', comparison);
      else
        console.log(
          `[API Response] ${response.config.method?.toUpperCase()} ${redactSensitiveUrl(response.config.url)}`,
          {
            status: response.status,
            data: redactSensitiveData(response.data),
          }
        );
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;
    const owner = originalRequest?._sessionOwner;
    if (owner !== undefined) assertSessionOwner(owner);

    // Log error in debug mode
    if (isDebugMode()) {
      const comparison = comparisonDiagnostic(
        error.config?.url,
        error.config?.method,
        error.response?.status,
        error.response?.data
      );
      if (comparison) console.error('[API Response Error]', comparison);
      else
        console.error('[API Response Error]', {
          url: redactSensitiveUrl(error.config?.url),
          status: error.response?.status,
          message: error.message,
          data: redactSensitiveData(error.response?.data),
        });
    }

    // Handle 401 Unauthorized - Token expired or invalid
    // Skip token refresh for auth endpoints (login, register, etc.)
    const isAuthEndpoint = originalRequest?.url?.startsWith('/auth/');
    if (error.response?.status === 401 && originalRequest && !isAuthEndpoint) {
      // Check if we've already tried to refresh
      const hasRetried = (originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })
        ._retry;

      if (!hasRetried) {
        (originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry = true;

        try {
          // Deduplicated refresh — concurrent 401s share a single in-flight request
          const tokens = await refreshAccessToken(async (rt) => {
            const response = await axios.post(`${getApiUrl()}/auth/refresh`, {
              refreshToken: rt,
            });
            return response.data;
          });

          assertSessionOwner(owner!);
          // Sync Zustand store with new tokens
          useAuthStore.getState().updateTokens({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
          });

          // Update the failed request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
          }

          // Retry the original request
          return apiClient(originalRequest);
        } catch (refreshError) {
          assertSessionOwner(owner!);
          expireSession(owner!);

          return Promise.reject(refreshError);
        }
      }

      expireSession(owner!);
    }

    // Handle other errors
    // This can be extended with error notification system
    return Promise.reject(error);
  }
);

/**
 * API Error class for type-safe error handling
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data: unknown,
    message?: string
  ) {
    super(message || statusText);
    this.name = 'ApiError';
  }
}

/**
 * Helper function to extract error message from API error
 */
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    // Check for API error response
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    // Check for validation errors
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      if (Array.isArray(errors)) {
        return errors.map((e: { message?: string }) => e.message || String(e)).join(', ');
      }
      return String(errors);
    }

    // Fallback to status text
    return error.response?.statusText || error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unknown error occurred';
};

/**
 * Type-safe API response wrapper
 */
// TODO: align with @skillspec/shared-types ApiResponse — actual API envelope is { data, meta?: { timestamp, requestId? } }
export interface ApiResponse<T> {
  data: T;
  message?: string;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

/**
 * Export the client as default
 */
export default apiClient;
